import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { Linkedin, Instagram } from "lucide-react";
import { teamMembers } from "@/data/teamData";

const values = [
  {
    title: "Unfiltered Honesty",
    description: "We tell the truth about branding, marketing, and agency practices, no matter how uncomfortable. We challenge industry lies and expose manipulative tactics.",
  },
  {
    title: "Strategic Depth",
    description: "We prioritize the 'Why, What, Who, and How' over mere aesthetics and vanity metrics. Our approach is always strategy-led (Diagnosis-Strategy-Tactics) to ensure sustainable and meaningful growth.",
  },
  {
    title: "Radical Authenticity",
    description: "We champion the \"You'est You\" philosophy. We believe true branding is built by embracing reality and weaknesses, requiring zero drama or acting from our clients.",
  },
  {
    title: "Accessibility",
    description: "We democratize high-quality, strategic brand and marketing solutions, making them affordable and accessible to the startups and SMEs who need them most.",
  },
  {
    title: "Educator Mindset",
    description: "We are dedicated to educating clients, designers, and entrepreneurs about the true power and practice of real branding, separating it from misconceptions in the market and within the industry.",
  },
];

const WhoPage = () => {
  return (
    <Layout>


      {/* Team Grid */}
      <section className="section-padding pt-32 lg:pt-40 violet-glow-section">
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mb-16">
            <Reveal trigger="onLoad" delay={0.1}>
              <p className="label bg-white text-purple px-3 py-1 inline-block mb-4">Our People</p>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.2}>
              <h2 className="display-md text-white mb-6">
                <span className="font-light">Collective</span> <span className="font-bold">Thinking.</span><br />
                <span className="font-light">Unified</span> <span className="font-bold">Direction.</span>
              </h2>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.3}>
              <p className="text-sm md:text-base lg:text-lg text-white/70 whitespace-nowrap">
                Our team is built around strategic thinking, creative depth, and focused execution.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8 lg:gap-12">
            {teamMembers.map((member, i) => (
              <div key={member.id} className="flex flex-col">
                <Reveal delay={i * 0.1} width="full" trigger="onLoad">
                  <Link to={`/team/${member.id}`} className="block group">
                    {/* The Card Rectangle */}
                    <div className="bg-white group-hover:bg-[#c0ff09] transition-all duration-300 rounded-sm mb-3 md:mb-6 overflow-hidden flex flex-col shadow-sm hover:shadow-md h-full">
                      {/* Header Info */}
                      <div className="p-3 md:p-6 lg:p-7 h-20 md:h-28 flex flex-col justify-start">
                        <h3 className="text-sm md:text-2xl font-bold mb-0.5 text-black tracking-tight leading-tight group-hover:text-black transition-colors">
                          {member.name}
                        </h3>
                        <p className="text-black/40 group-hover:text-black text-[8px] md:text-[10px] font-bold uppercase tracking-wider min-h-[1.25rem] leading-tight transition-colors">
                          {member.role}
                        </p>
                      </div>

                      {/* Team Image (Always Visible on Mobile Color, Grayscale on Desktop hover) */}
                      <div className="w-full aspect-[3/2.85] overflow-hidden bg-gray-100 flex items-center justify-center grayscale-0 lg:grayscale lg:group-hover:grayscale-0 transition-all duration-500 relative shrink-0">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                          loading="eager"
                        />
                      </div>
                    </div>
                  </Link>
                </Reveal>

                {/* Social Icons (Outside the rectangle) */}
                <Reveal delay={0.2 + (i * 0.1)} width="full" className="px-1" trigger="onLoad">
                  <div className="flex gap-3 md:gap-4">
                    {member.instagram && (
                      <a
                        href={member.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-white/80 transition-colors"
                        aria-label="Instagram"
                      >
                        <Instagram className="w-4 h-4 md:w-5 md:h-5" />
                      </a>
                    )}
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-white/80 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="w-4 h-4 md:w-5 md:h-5" />
                      </a>
                    )}
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white border-t border-black/5">
        <div className="container-wide">
          <div className="max-w-3xl mb-16">
            <Reveal className="mb-4">
              <p className="label bg-purple text-white px-3 py-1 inline-block">Our Culture</p>
            </Reveal>
            <Reveal className="mb-6 pb-2">
              <h2 className="display-md text-black">What we stand for.</h2>
            </Reveal>
            <Reveal>
              <p className="body-lg text-black/60">
                Our values aren't wall decorations. They're decision-making
                frameworks that guide how we work with clients and each other.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <Reveal key={value.title} delay={index * 0.1}>
                <div className="flex gap-6 h-full">
                  <span className="text-5xl font-bold text-black/10 leading-none">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-black">
                      {value.title}
                    </h3>
                    <p className="text-black/60 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-black border-t border-white/5">
        <div className="container-narrow text-center">
          <Reveal className="mx-auto mb-8 pb-4 !overflow-visible">
            <h2 className="display-md text-white">
              Want to work together?
            </h2>
          </Reveal>
          <Reveal className="mx-auto" delay={0.2}>
            <Button className="btn-primary" asChild>
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default WhoPage;
